import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, logoImg, gbp } from "@/lib/api";
import { CardTile, CardGrid } from "@/components/CardTile";
import { PageTitle, ProgressBar, Spinner, Empty, Btn } from "@/components/UI";
import { CollectionTabs } from "@/components/CollectionTabs";
import { CardView, FilterPanel, cardFields, filterCards } from '@/components/CardBrowser';
import { displayCards } from '@/lib/setCards';
import { useRememberedState } from '@/hooks/useRememberedState';

export function Goals() {
  const [goals, setGoals] = useState(null);
  useEffect(() => { api.get("/goals").then((r) => setGoals(r.data)); }, []);
  if (!goals) return <Spinner />;
  return (
    <div data-testid="goals-page">
      <PageTitle title="Set goals" sub="Sets you're collecting and exactly what's left to find." />
      {goals.length === 0 ? (
        <Empty testid="goals-empty" title="No goals yet" text="Open a set and choose Base, Full or Master to start tracking it."><Link to="/sets"><Btn>Browse sets</Btn></Link></Empty>
      ) : (
        <div className="grid gap-5 md:grid-cols-2">
          {goals.map((g) => {
            const [have, total] = g.progress[g.target];
            return (
              <div key={g.set_id} className="rise rounded-3xl border border-slate-200/60 bg-white p-6 shadow-[0_4px_12px_rgba(0,0,0,0.05)]" data-testid={`goal-card-${g.set_id}`}>
                <div className="flex items-center gap-4">
                  {g.logo && <img src={logoImg(g.logo)} alt="" className="h-14 w-28 object-contain" />}
                  <div className="min-w-0"><p className="truncate font-display text-xl font-black">{g.name}</p><p className="text-sm text-slate-500">{g.serie} · {g.releaseDate}</p></div>
                </div>
                <div className="mt-5 space-y-3">
                  <ProgressBar size="h-6" label={`${g.target} goal`} value={have} total={total} />
                  {g.target !== "base" && <ProgressBar size="h-2" label="Base" value={g.progress.base[0]} total={g.progress.base[1]} />}
                </div>
                <div className="mt-5 flex items-center justify-between">
                  <p className="font-display font-bold">{have >= total ? "Complete!" : `${total - have} to go`}</p>
                  <div className="flex gap-2">
                    <Link to={`/sets/${g.set_id}?view=missing`}><Btn className="px-4 py-2 text-sm" data-testid={`goal-missing-${g.set_id}`}>See missing</Btn></Link>
                    <Link to={`/sets/${g.set_id}`}><Btn variant="ghost" className="px-4 py-2 text-sm">Open</Btn></Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function Wishlist() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useRememberedState('filters', {});
  useEffect(() => { api.get("/wishlist").then((r) => setItems(r.data)); }, []);
  if (!items) return <Spinner />;
  const total = items.reduce((a, i) => a + (i.price_gbp || 0), 0);
  return <CardView view="wishlist" controls={false}>{({ display, editButton, editor }) => (
    <div data-testid="wishlist-page">
      <CollectionTabs />
      <PageTitle title="Wishlist" sub={`Cards you're hunting for · about ${gbp(total)} at market price.`}>{editButton}</PageTitle>{editor}
      <FilterPanel value={filter} onChange={setFilter} fields={cardFields(items)} />
      {items.length === 0 ? <Empty testid="wishlist-empty" title="Wishlist is empty" text="Tap the heart on any card to save it here." /> : (
        <CardGrid testid="wishlist-grid">
          {filterCards(displayCards(items, display), filter, { expandPrices: false }).map((it, i) => <CardTile key={it.key} index={i} card={{ ...it, id: it.card_id }} variant={it.variant} owned={it.owned} price={it.price_gbp} />)}
        </CardGrid>
      )}
    </div>
  )}</CardView>;
}
